import api from '@api';
import SourceOverview from '@components/layout/main/SourceOverview';
import { useWindowSize } from '@hooks';
import ChevronIcon from '@icons/chevron.svg?react';
import ThinkingOverviewIcon from '@icons/data-metrics.svg?react';
import { useChatStore } from '@store/chat';
import type { PropsWithChildren } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Remark } from 'react-remark';
import type { SourcesOverviewResponse } from 'types/schema';
import Styled from './ThinkingOverview.styled';

interface Props {
  replyId: string;
  onLoad?: (data: SourcesOverviewResponse) => void;
}

function ThinkingOverview({ replyId, onLoad }: Props) {
  const chatId = useChatStore((store) => store.chat.id);
  const windowSize = useWindowSize();
  const [overviewData, setOverviewData] =
    useState<SourcesOverviewResponse | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [summary, setSummary] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setIsExpanded((isExpanded) => !isExpanded);
  };

  useEffect(() => {
    if (overviewData === null) {
      api.sources.getOverviewData(replyId).then((data) => {
        setOverviewData(data);
        onLoad?.(data);
      });
    }
  }, [overviewData, replyId, onLoad]);

  useEffect(() => {
    if (summary === null) {
      // Note: The summary data is generated asynchronously by the server and
      // may not be available immediately after the agent's reply
      api.sources.getSummary(replyId).then(setSummary);
    }
  }, [summary, replyId]);

  useEffect(() => {
    if (windowSize.value) {
      setIsExpanded(false);
    }
  }, [windowSize.value]);

  useEffect(() => {
    if (isExpanded) {
      api.analytics.ViewedThinkingOverview({
        ['chat_id']: chatId
      });
    }
  }, [chatId, isExpanded]);

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      setContentHeight(entry.target.scrollHeight);
    });

    observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, []);

  const summaryContent = summary ? (
    <Remark rehypeReactOptions={rehypeReactOptions}>{summary}</Remark>
  ) : (
    <Styled.Skeleton className="fade-in-out">
      {(['95%', '100%', '87%'] as const).map((width, index) => (
        <Styled.SkeletonItem
          key={`summary-skeleton-item-${index}`}
          width={width}
        />
      ))}
    </Styled.Skeleton>
  );

  const overviewContent = overviewData ? (
    <Fragment>
      <Styled.Separator />
      <Styled.ContentTitle>Overview of GWI data used</Styled.ContentTitle>
      <SourceOverview data={overviewData} />
    </Fragment>
  ) : null;

  return (
    <Styled.Wrapper
      data-testid="thinking-overview"
      height={contentHeight}
      expanded={isExpanded}>
      <Styled.Header
        data-testid="thinking-overview-header"
        onClick={handleToggle}>
        <ThinkingOverviewIcon />
        <Styled.Title>Show thinking</Styled.Title>
        <Styled.Arrow>
          <ChevronIcon />
        </Styled.Arrow>
      </Styled.Header>
      <Styled.Content data-testid="thinking-overview-content">
        <Styled.ContentInner ref={contentRef}>
          <Styled.ContentTitle>
            What happened in the background
          </Styled.ContentTitle>
          {summaryContent}
          {overviewContent}
        </Styled.ContentInner>
      </Styled.Content>
    </Styled.Wrapper>
  );
}

const rehypeReactOptions = {
  components: {
    ul: ({ children }: PropsWithChildren) => (
      <Styled.SummaryList>{children}</Styled.SummaryList>
    ),
    li: ({ children }: PropsWithChildren) => (
      <Styled.SummaryListItem>{children}</Styled.SummaryListItem>
    ),
    p: ({ children }: PropsWithChildren) => (
      <Styled.SummaryText>{children}</Styled.SummaryText>
    )
  }
};

export default ThinkingOverview;
